/**
 * CJK Font Loader
 *
 * Loads appropriate CJK fonts from Google Fonts based on language region and user preference.
 */

export type CJKRegion = "JP" | "SC" | "TC" | "HK" | "KR";
export type CJKFontFamily =
  | "Noto Sans"
  | "Noto Serif"
  | "Handwriting"
  | "System Default";

/** Track which fonts have been loaded to avoid duplicate requests */
const loadedFonts = new Set<string>();

/** Noto Serif HK doesn't exist, so fall back to TC */
function getSerifRegion(region: CJKRegion): CJKRegion {
  return region === "HK" ? "TC" : region;
}

/** Handwriting font names and URLs vary by region (all from Google Fonts) */
const characterFontConfig: Record<
  CJKRegion,
  { name: string; url: string } | null
> = {
  JP: {
    name: "Yuji Syuku",
    url: "https://fonts.googleapis.com/css2?family=Yuji+Syuku&display=swap",
  },
  SC: {
    name: "Ma Shan Zheng",
    url: "https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&display=swap",
  },
  TC: {
    name: "Zhi Mang Xing",
    url: "https://fonts.googleapis.com/css2?family=Zhi+Mang+Xing&display=swap",
  },
  HK: {
    name: "Zhi Mang Xing",
    url: "https://fonts.googleapis.com/css2?family=Zhi+Mang+Xing&display=swap",
  },
  KR: {
    name: "Nanum Pen Script",
    url: "https://fonts.googleapis.com/css2?family=Nanum+Pen+Script&display=swap",
  },
};

/** System font stacks for each CJK region */
const systemFontStacks: Record<CJKRegion, string> = {
  JP: '"Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
  SC: '"PingFang SC", "Microsoft YaHei", sans-serif',
  TC: '"PingFang TC", "Microsoft JhengHei", sans-serif',
  HK: '"PingFang TC", "Microsoft JhengHei", sans-serif',
  KR: '"Apple SD Gothic Neo", "Malgun Gothic", sans-serif',
};

/**
 * Get the Google Fonts URL for a given font family and region.
 */
function getFontUrl(
  fontFamily: CJKFontFamily,
  region: CJKRegion
): string | null {
  switch (fontFamily) {
    case "Noto Sans":
      return `https://fonts.googleapis.com/css2?family=Noto+Sans+${region}:wght@400;700&display=swap`;

    case "Noto Serif":
      return `https://fonts.googleapis.com/css2?family=Noto+Serif+${getSerifRegion(region)}:wght@400;700&display=swap`;

    case "Handwriting":
      return characterFontConfig[region]?.url ?? null;

    case "System Default":
      return null;

    default:
      return null;
  }
}

/**
 * Load CJK fonts for a given font family and region.
 * Returns a promise that resolves when the fonts are loaded.
 */
export async function loadFontsForRegion(
  fontFamily: CJKFontFamily,
  region: CJKRegion
): Promise<void> {
  const fontUrl = getFontUrl(fontFamily, region);

  if (!fontUrl) {
    return;
  }

  const cacheKey = `${fontFamily}-${region}`;
  if (loadedFonts.has(cacheKey)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;

  return new Promise((resolve, reject) => {
    link.onload = () => {
      loadedFonts.add(cacheKey);
      resolve();
    };
    link.onerror = () => {
      console.error(`Failed to load CJK font: ${fontUrl}`);
      reject(new Error(`Failed to load font: ${fontUrl}`));
    };
    document.head.appendChild(link);
  });
}

/**
 * Get the CSS font-family string for a given font family and region.
 */
export function getCSSFontFamily(
  fontFamily: CJKFontFamily,
  region: CJKRegion
): string {
  switch (fontFamily) {
    case "Noto Sans":
      return `"Noto Sans ${region}", sans-serif`;

    case "Noto Serif":
      return `"Noto Serif ${getSerifRegion(region)}", serif`;

    case "Handwriting": {
      const config = characterFontConfig[region];
      return config ? `"${config.name}", sans-serif` : "sans-serif";
    }

    case "System Default":
      return systemFontStacks[region] ?? "sans-serif";

    default:
      return "sans-serif";
  }
}

/** List of available CJK font options */
export const cjkFontOptions: CJKFontFamily[] = [
  "Noto Sans",
  "Noto Serif",
  "Handwriting",
  "System Default",
];
